import { z } from 'zod';

import { finNoAnteriorAInicioOpcional } from '@/shared/lib';

/**
 * Schemas del reporte de DETALLE (ADR-049). Espejan los DTOs del backend
 * (`FiltrosReporteDetalleDto` + `SolicitarReporteDetalleDto`) para que el
 * usuario vea el error antes de gastar una de las 3 solicitudes activas que el
 * servidor le permite.
 *
 * Los topes de VOLUMEN (días del rango, cantidad de incidencias) NO se validan
 * aquí: dependen de configuración del servidor y de los datos, y llegan por
 * `GET …/detalle/estimacion` (ver `LimitesReporteDetalle`). Duplicarlos como
 * constantes del cliente sería adivinar; el flujo correcto es estimar y, si aun
 * así se manda, leer el 422.
 */

export const formatoReporteDetalleSchema = z.enum(['pdf', 'xlsx', 'csv']);

/**
 * Estados del filtro. Se declara como `string` y no como el enum de
 * `entities/incidencia` a propósito: un entity no importa a otro, y el backend
 * ya devuelve `estados: string[]` en los filtros sellados. La lista de opciones
 * (y su etiqueta) la aporta la capa que arma el formulario, que sí puede leer
 * ambos dominios.
 */
export const filtrosReporteDetalleSchema = z
  .object({
    proyectoId: z.coerce.number().int().positive().optional(),
    /** `YYYY-MM-DD` (el adapter lo expande al instante ISO del día). */
    desde: z.string().optional(),
    hasta: z.string().optional(),
    estados: z.array(z.string()).optional(),
  })
  .refine(
    ...finNoAnteriorAInicioOpcional(
      'desde',
      'hasta',
      'La fecha final no puede ser anterior a la inicial.',
    ),
  );

export type FiltrosReporteDetalleInput = z.infer<typeof filtrosReporteDetalleSchema>;

/** Body del `POST /reportes/incidencias/detalle`: filtros + formato. */
export const solicitarReporteDetalleSchema = z.object({
  formato: formatoReporteDetalleSchema,
  proyectoId: z.coerce.number().int().positive().optional(),
  desde: z.string().optional(),
  hasta: z.string().optional(),
  estados: z.array(z.string()).optional(),
});

export type SolicitarReporteDetalleInput = z.infer<typeof solicitarReporteDetalleSchema>;

/** Query del historial "mis reportes". */
export const filtrosHistorialReporteDetalleSchema = z.object({
  estado: z.enum(['Pendiente', 'Procesando', 'Listo', 'Fallido', 'Expirado']).optional(),
  page: z.number().int().positive().optional(),
  limit: z.number().int().positive().optional(),
});

export type FiltrosHistorialReporteDetalleInput = z.infer<
  typeof filtrosHistorialReporteDetalleSchema
>;
